import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories, categoryLabels } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = slug ? categoryLabels[slug] || slug : '';

  const filteredComics = comics
    .filter((c) => c.category === slug)
    .sort((a, b) => {
      if (sortBy === 'popular') return b.views - a.views;
      if (sortBy === 'latest') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return b.rating - a.rating;
    });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const pageTitle = `${categoryName} - 免费在线观看`;
  const pageDesc = `韩漫屋${categoryName}频道，收录${currentCategory?.count || 0}部精选韩国漫画，免费在线观看，下拉式阅读界面，持续更新中。`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${categoryName}, 韩国漫画, 免费韩漫在线观看, 韩漫屋, 下拉式`}
        canonicalUrl={`https://hanmanwu.com/category/${slug}`}
      />

      <Navbar />

      <main>
        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />
        </div>

        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {filteredComics.length} 部作品
                </p>
              </div>

              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                {[
                  { key: 'popular', label: '最热' },
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key as typeof sortBy)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setShowMobileFilter(!showMobileFilter)}
                className="sm:hidden px-4 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 flex items-center gap-1 whitespace-nowrap"
              >
                <i className="ri-equalizer-line" /> 排序筛选
              </button>
            </div>

            {showMobileFilter && (
              <div className="sm:hidden flex items-center gap-2 mb-6 flex-wrap">
                {[
                  { key: 'popular', label: '最热' },
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => {
                      setSortBy(opt.key as typeof sortBy);
                      setShowMobileFilter(false);
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            <div className="hidden sm:flex items-center gap-2 mb-8 flex-wrap">
              <span className="text-xs text-foreground-400 mr-1">全部分类：</span>
              <Link
                to="/"
                className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap"
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - ${categoryName}`}
                        title={`${comic.title} - 韩国漫画免费在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded">
                          {comic.rating}
                        </span>
                      </div>
                      {comic.status === '已完结' && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 bg-foreground-800/70 text-background-50 text-[10px] font-medium rounded">
                            完结
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-foreground-500">{comic.author}</p>
                      <p className="text-xs text-foreground-400">{comic.chapters}话</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类暂无漫画作品</p>
                <Link
                  to="/"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}

            {filteredComics.length === 0 && slug && !categories.find((c) => c.slug === slug) && (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">未找到该分类</p>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="px-4 py-2 bg-background-100 text-foreground-600 rounded-full text-xs font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}